import { Link } from 'react-router-dom';
import { ArrowRight, CheckCircle, ShieldCheck, CurrencyDollar, ChartLineUp, Headset, Globe, UsersFour } from '@phosphor-icons/react';

const benefits = [
  { icon: CurrencyDollar, title: 'Competitive Commissions', desc: 'Earn industry-leading commissions for every student successfully placed through our platform.' },
  { icon: ShieldCheck, title: 'Secure Portal', desc: 'Access a dedicated recruiter portal to submit students, upload documents, and track applications.' },
  { icon: ChartLineUp, title: 'Real-Time Tracking', desc: 'Monitor the status of every student application from submission to enrollment in real time.' },
  { icon: Headset, title: 'Dedicated Support', desc: 'Get priority support from our partnership team for any questions or assistance needed.' },
  { icon: Globe, title: 'Global Network', desc: 'Join a growing network of recruitment partners across 15+ countries worldwide.' },
  { icon: UsersFour, title: 'Training & Resources', desc: 'Access training materials, marketing resources, and regular webinars to grow your business.' },
];

const steps = [
  { num: '01', title: 'Sign Up', desc: 'Complete the recruiter sign-up form with your details and experience.' },
  { num: '02', title: 'Agreement Review', desc: 'Our team reviews your application and sends a partnership agreement for your signature.' },
  { num: '03', title: 'Get Activated', desc: 'Once the agreement is signed, your portal account is activated and ready to use.' },
  { num: '04', title: 'Submit Students', desc: 'Start creating student profiles, uploading documents, and submitting applications.' },
];

export default function ForRecruiters() {
  return (
    <div data-testid="for-recruiters-page">
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">For Recruiters & Agents</p>
              <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 tracking-tight mb-6">Partner With Study Alliance</h1>
              <p className="text-base lg:text-lg text-gray-600 leading-relaxed mb-8">
                Join our network of trusted recruitment partners. Submit students, track applications, and earn competitive commissions through our secure recruiter portal.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link to="/recruiter-signup" className="inline-flex items-center gap-2 bg-blue-800 text-white px-6 py-3 rounded-md font-medium hover:bg-blue-700 transition-all hover:-translate-y-0.5" data-testid="recruiter-signup-btn">
                  Become a Partner <ArrowRight size={18} />
                </Link>
                <Link to="/contact" className="inline-flex items-center gap-2 border border-gray-300 text-gray-700 px-6 py-3 rounded-md font-medium hover:bg-gray-100 transition-all" data-testid="recruiter-contact-btn">
                  Contact Us
                </Link>
              </div>
            </div>
            <div className="lg:col-span-6">
              <img src="https://images.unsplash.com/photo-1758518730384-be3d205838e8?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwxfHxidXNpbmVzcyUyMHByb2Zlc3Npb25hbCUyMGhhbmRzaGFrZXxlbnwwfHx8fDE3NzYzNjU1NjR8MA&ixlib=rb-4.1.0&q=85" alt="Business handshake" className="rounded-lg shadow-lg w-full object-cover max-h-[440px]" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Why Partner With Us</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">Partnership Benefits</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map(b => (
              <div key={b.title} className="p-6 bg-white border border-gray-200 rounded-lg hover:shadow-lg hover:-translate-y-1 transition-all duration-300">
                <div className="w-12 h-12 bg-blue-50 rounded-lg flex items-center justify-center mb-4">
                  <b.icon size={24} className="text-blue-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{b.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{b.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <p className="text-blue-600 font-medium text-sm uppercase tracking-widest mb-3">Getting Started</p>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 tracking-tight">How to Become a Partner</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {steps.map(s => (
              <div key={s.num} className="p-6 bg-white border border-gray-200 rounded-lg">
                <span className="text-3xl font-bold text-blue-500/20" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.num}</span>
                <h3 className="text-lg font-semibold text-gray-900 mt-2 mb-2" style={{ fontFamily: "'Libre Baskerville', serif" }}>{s.title}</h3>
                <p className="text-gray-500 text-sm leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-blue-800 text-center">
        <div className="max-w-3xl mx-auto px-4">
          <h2 className="text-3xl font-bold text-white tracking-tight mb-4">Ready to Partner?</h2>
          <p className="text-gray-300 mb-8">Sign up today and start submitting students through our secure recruiter portal.</p>
          <Link to="/recruiter-signup" className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-3 rounded-md font-medium hover:bg-blue-700 transition-all" data-testid="recruiter-cta-btn">
            Recruiter Sign Up <ArrowRight size={18} />
          </Link>
        </div>
      </section>
    </div>
  );
}
